import { useState, useEffect, useCallback } from 'react';
import UpdateCategoryForm from "../../components/update/update-categories-form";
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner';
import Loading from '../../cors/Loading'; 
import { useLanguage } from "../../context/LanguageProvider";

export default function Page() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategory = useCallback(async () => {
    if (!id) {
      setError(t('categories.id_missing'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);   
    try {
      if (!token) {
        toast.error(t('errors.auth_token_missing'));
        setError(t('errors.auth_token_missing'));
        setLoading(false);
        return;
      }

      const res = await fetch(`${BASE_URL}/api/categories/${id}`, { 
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`, 
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const errorMessage = errorData.message || `${t('categories.fetch_failed')} : ${res.statusText}`;
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.status && data.data) {
        setCategory(data.data); // Set the fetched category data
      } else {
        throw new Error(data.message || t('categories.unexpected_format'));
      }
    } catch (err) {
      console.error("Erreur lors de la récupération de la catégorie :", err); // French: Error fetching category
      setError(err.message || t('categories.loading_error'));
      toast.error(`${t('messages.error')} : ${err.message || t('categories.loading_error')}`);
    } finally {
      setLoading(false); // End loading regardless of success or failure
    }
  }, [id, token, t]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <h2 className="text-2xl font-bold text-red-700 mb-4">{t('categories.error_loading')}</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={fetchCategory} 
            className="px-6 py-3 bg-[var(--color-primary-500)] text-white font-semibold rounded-lg hover:bg-[var(--color-primary-400)] transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">{t('categories.not_found')}</h2>
          <p className="text-gray-600">{t('categories.not_found_desc')} "{id}"</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl max-xl:text-3xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
            {t('categories.update_title')}
          </h1>
          <p className="text-sm max-xl:text-sm text-gray-600 max-w-2xl mx-auto">
            {t('categories.update_subtitle')}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <UpdateCategoryForm category={category} />
        </div>
      </div>
    </div>
  );
}
